import React, { useRef, useState, useEffect } from 'react';
import './HorizontalScrollIndicator.scss';

interface HorizontalScrollIndicatorProps {
  children: React.ReactNode;
}

const HorizontalScrollIndicator: React.FC<HorizontalScrollIndicatorProps> = ({ children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let scrollEl: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const checkScroll = () => {
      if (!scrollEl) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
      setShowLeft(scrollLeft > 1);
      setShowRight(scrollLeft + clientWidth < scrollWidth - 20);
    };

    const setupScrollListener = () => {
      let el = wrapper.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
      if (!el) {
        el = wrapper.querySelector('.MuiDataGrid-main') as HTMLElement;
      }
      if (!el) {
        const allDivs = wrapper.getElementsByTagName('div');
        for (let i = 0; i < allDivs.length; i++) {
          const style = window.getComputedStyle(allDivs[i]);
          if (style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflow === 'auto' || style.overflow === 'scroll') {
            el = allDivs[i];
            break;
          }
        }
      }

      if (el === scrollEl) return;

      if (scrollEl) {
        scrollEl.removeEventListener('scroll', checkScroll);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      }

      scrollEl = el;

      if (scrollEl) {
        checkScroll();
        scrollEl.addEventListener('scroll', checkScroll, { passive: true });

        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(scrollEl);
        if (scrollEl.firstElementChild) {
          resizeObserver.observe(scrollEl.firstElementChild as Element);
        }
      } else {
        setShowLeft(false);
        setShowRight(false);
      }
    };

    setupScrollListener();

    const mutationObserver = new MutationObserver(() => {
      setupScrollListener();
    });
    mutationObserver.observe(wrapper, { childList: true, subtree: true });

    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', checkScroll);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      mutationObserver.disconnect();
    };
  }, [children]);

  return (
    <div className="custom-datagrid-wrapper" ref={wrapperRef}>
      <div className={`custom-datagrid-wrapper-inner-left-panel ${showLeft ? 'visible' : ''}`}>
        <div className="indicator indicator-left">
          <span className='highlighted-gonden-indicator'></span>
          <span className="highlighted-indicator"></span>
          <span></span>
        </div>
      </div>
      <div className={`custom-datagrid-wrapper-inner-right-panel ${showRight ? 'visible' : ''}`}>
        <div className="indicator indicator-right">
          <span></span>
          <span className="highlighted-indicator"></span>
          <span className='highlighted-gonden-indicator'></span>
        </div>
      </div>
      {children}
    </div>
  );
};

export default HorizontalScrollIndicator;
export { HorizontalScrollIndicator };
